import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TableIndication, TableRowAction } from '@app/shared/models/table-action-type.model';
import { Subject } from 'rxjs';
import { ColumnDefinitionModel } from '@app/shared/models/column-definition.model';
import { PreferenceService } from '@app/core/services/preference.service';
import { TablePreferencesModel } from '@app/core/models/preferences/table-preferences.model';
import { filter, takeUntil } from 'rxjs/operators';
import { TableRowSelection } from '@app/shared/models/table-row-selection.model';
import { ColumnDefinitionPredefined } from '@app/shared/models/column-definition-predefined.model';
import { TableNavigationModel } from '@app/shared/models/table-navigation.model';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ExcelService } from '@app/core/services/excel.service';
import { DynamicPipe } from '@app/shared/pipes/dynamic.pipe';
import { SessionStorageService } from '@app/core/services/session-storage.service';
import { PreferenceConverterHelper } from '@app/shared/helper/preference-converter-helper';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class TableComponent implements OnInit, OnChanges, OnDestroy {

  @Input() configKey: string;
  @Input() exportFileName: string
  @Input() data: any[] = [];
  @Input() columnsDefinition: ColumnDefinitionModel[] = [];
  @Input() tableActions: TableRowAction[] = [];
  @Input() tableIndications: TableIndication[] = [];
  @Input() pageSize: number = 20;
  @Input() pageIndex: number = 0;
  @Input() length: number = 10;
  @Input() serverPagination: boolean = false;
  @Input() persistFilter: boolean = true;
  @Input() highlights: any[] = [];
  @Input() onDataSourceChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() onChangeStatus = (data: any) => { };
  @Input() onClearFilters = () => { };
  @Input() isActiveReadOnly : boolean = false;

  @Input() onFiltersCleared: EventEmitter<void> = new EventEmitter<void>();
  @Input() onExportExcelCaller: EventEmitter<void> = new EventEmitter<void>();

  @Input() showFilter: boolean = true;

  @Input() onExport = () => { };
  @Input() onExportDataSourceAvailable: EventEmitter<any> = new EventEmitter<any>();

  @Input() showNewButton: boolean = false;
  @Input() showImportButton: boolean = false;
  @Input() showSettingsButton: boolean = false;
  @Input() showClearFilterButton: boolean = false;
  @Input() onNew = () => { };
  @Input() onImport = () => { };
  @Input() onSettings = () => { };

  @Output() onSortChange: EventEmitter<Sort> = new EventEmitter<Sort>();
  @Output() onPageChange: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
  @Output() onRowSelectionChange: EventEmitter<string[]> = new EventEmitter<string[]>();
  @Output() onAllRowSelectionChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  pageNavigationStorageKey: string;
  referenceColumnsDefinition: ColumnDefinitionModel[] = [];
  displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<any>();
  unsubscribe$: Subject<boolean> = new Subject<boolean>();
  tablePreferenceConfigKey: string;
  hideableColumns: ColumnDefinitionModel[];
  selectedRows: TableRowSelection[] = [];
  hasSelectionFeature: boolean = false;
  tableNavigation: TableNavigationModel;
  exportExcludedColumns: string[];
  isAllSelected: boolean = false;

  keyColumn: string;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  constructor(
    private dynamicPipe: DynamicPipe,
    private preferenceService: PreferenceService,
    private excelService: ExcelService,
    private sessionStorageService: SessionStorageService,
    private converterHelper: PreferenceConverterHelper
  ) {
    this.tableNavigation = new TableNavigationModel();
    this.exportExcludedColumns = ['Selection', 'Action', 'SupplierHasWarning', 'Indication'];
  }

  ngOnInit(): void {
    this.dataSource.data = this.data;
    if (!this.serverPagination) {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
      this.dataSource.sortingDataAccessor = (item, property) => {
        switch (property) {
          case 'InvoiceWorkflowStageValidators.FirstValidatorUser':
            return item.InvoiceWorkflowStageValidators.FirstValidatorUser;
          case 'InvoiceWorkflowStageValidators.SecondValidatorUser':
            return item.InvoiceWorkflowStageValidators.SecondValidatorUser;
          case 'InvoiceWorkflowStageValidators.FirstValidation':
            return item.InvoiceWorkflowStageValidators.FirstValidation;
          case 'InvoiceWorkflowStageValidators.SecondValidation':
            return item.InvoiceWorkflowStageValidators.SecondValidation;
          default:
            return item[property];
        }
      };
    }

    this.tablePreferenceConfigKey = `table_config_${this.configKey}`;
    this.pageNavigationStorageKey = `table_grid_${this.configKey}_keyword`;

    const configuredColumnsDefinition = _.cloneDeep(this.columnsDefinition);
    this.columnsDefinition = this.getColumnsDefinition();
    const newColumnsDefinition = _.differenceBy(configuredColumnsDefinition, this.columnsDefinition, c => c.ColumnName);
    this.columnsDefinition.push(...newColumnsDefinition);

    this.referenceColumnsDefinition = _.cloneDeep(this.columnsDefinition);
    this.dataSource.filterPredicate = this.filterByVisibleColumns;
    this.displayedColumns = this.columnsDefinition.filter(c => c.IsVisible).map(c => c.ColumnName);
    this.hideableColumns = this.columnsDefinition.filter(c => c.CanHide);

    this.tableNavigation = this.getGridNavigationFilters();

    this.hasSelectionFeature = this.columnsDefinition.filter(c => c.Position == ColumnDefinitionPredefined.SELECT).length > 0;

    if (this.hasSelectionFeature) {
      this.keyColumn = _.head(this.columnsDefinition.filter(c => c.Position == ColumnDefinitionPredefined.SELECT)).ColumnName;
      this.initSelectedRows();
    }
    this.applyNavigationFilter();
  }

  ngAfterViewInit(): void {
    this.onExportDataSourceAvailable.pipe(
      takeUntil(this.unsubscribe$),
      filter(data => !_.isNil(data) && !_.isEmpty(data)))
      .subscribe((data: any) => {
        this.excelService.exportAsExcel({
          fileName: `${this.exportFileName}_${moment().format('YYYYMMDo')}`,
          data: this.transformData(data)
        });
      })

    this.onDataSourceChanged
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((resetPageIndex: boolean) => {
        if (!_.isNil(resetPageIndex) && resetPageIndex) {
          this.paginator.pageIndex = 0;
        }

        this.resetSelection();
      });

    this.sort.sortChange.subscribe((sort: Sort) => {
      if (this.serverPagination) {
        this.onSortChange.emit(sort);
        this.paginator.pageIndex = 0;
        this.resetSelection();
      }
      this.tableNavigation.Sort = sort;
      this.saveTablePreferences();
    });

    this.paginator.page.subscribe((page: PageEvent) => {
      if (this.serverPagination) {
        this.onPageChange.emit(page);
        this.pageSize = page.pageSize;
      }
      if (this.tableNavigation?.Page?.pageSize !== page?.pageSize || this.tableNavigation?.Page?.pageIndex !== page?.pageIndex) {
        this.tableNavigation.Page = page;
        this.saveTablePreferences();
      }
    });

    if (this.pageIndex > 0) {
      setTimeout(() => {
        for (let index = 0; index < this.pageIndex; index++) {
          this.paginator.nextPage();
        }
      }, 1000);
    }

    this.onFiltersCleared
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(_ => {
        this.applyFilterValue('');
      });

    this.onExportExcelCaller
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(_ => {
        this.exportAsExcel()
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!_.isNil(changes.data?.currentValue)) {
      this.dataSource.data = changes.data.currentValue;
      this.resetSelection();
    }
    if (!_.isNil(changes.highlights?.currentValue) && _.isArray(changes.highlights.currentValue) && changes.highlights.currentValue.length > 0) {
      this.isAllSelected = false;
      this.onAllRowSelectionChange.emit(false);
      const highlightsKey = changes.highlights.currentValue.map((value) => value[this.keyColumn]);
      for (let index = 0; index < this.data.length; index++) {
        let key = this.data[index][this.keyColumn];
        if (key) {
          this.selectedRows[key] = new TableRowSelection(key, highlightsKey.includes(key));
        }
      }
      this.onRowSelectionChange.emit(highlightsKey);
    }
    else if (!_.isNil(changes.highlights?.previousValue) && _.isArray(changes.highlights.previousValue) && changes.highlights.previousValue.length > 0) {
      this.resetSelection();
    }
  }

  initSelectedRows(): void {
    this.isAllSelected = false;
    this.selectedRows = [];

    if (_.isNil(this.data) || !this.data.length) {
      return;
    }
    for (let index = 0; index < this.data.length; index++) {
      let key = this.data[index][this.keyColumn];
      if (key) {
        this.selectedRows[key] = new TableRowSelection(key, false);
      }
    }
  }

  applyFilter(event: Event): void {
    let filterValue = (event.target as HTMLInputElement).value;
    this.applyFilterValue(filterValue);
  }

  applyFilterValue(filterValue: string): void {
    this.filterOnValue(filterValue);
    this.tableNavigation.Keyword = filterValue;
    this.saveGridFilter(filterValue);

  }

  drop(event: CdkDragDrop<string[]>): void {
    let minIndex = this.columnsDefinition.filter((col: ColumnDefinitionModel) => col.Position <= 0).length;
    if (event.previousIndex < minIndex)
      return;

    const sourceColumn = this.displayedColumns[event.previousIndex];
    const destinationColumn = this.displayedColumns[event.currentIndex];

    const columnPreviousIndex = _.findIndex(this.columnsDefinition, c => c.ColumnName === sourceColumn);
    const columnCurrentIndex = _.findIndex(this.columnsDefinition, c => c.ColumnName === destinationColumn);

    moveItemInArray(this.displayedColumns, event.previousIndex, event.currentIndex);
    moveItemInArray(this.columnsDefinition, columnPreviousIndex, columnCurrentIndex);

    let positionIndex = 1;
    this.columnsDefinition.forEach((column: ColumnDefinitionModel) => {
      if (column.Position > 0)
        column.Position = positionIndex++;
    });

    this.saveTablePreferences();
  }

  onDisplayColumnsMenuClosed(): void {
    if (!_.isEqualWith(this.columnsDefinition, this.referenceColumnsDefinition)) {
      this.displayedColumns = this.columnsDefinition.filter(c => c.IsVisible).map(c => c.ColumnName);
      this.saveTablePreferences();
      this.applyNavigationFilter();
    }
  }

  saveTablePreferences(): void {
    const configuration = JSON.stringify({ columnsDef: this.columnsDefinition, pageDef: this.tableNavigation });
    localStorage.setItem(this.tablePreferenceConfigKey, configuration);

    const columnsDefLight = this.converterHelper.convertTocolumnsDefLight(this.columnsDefinition);
    const configurationLight = JSON.stringify({ columnsDef: columnsDefLight, pageDef: this.tableNavigation });

    this.preferenceService.saveTablePreferences({
      TableId: this.configKey,
      Configuration: configurationLight
    } as TablePreferencesModel)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe();

    this.referenceColumnsDefinition = _.cloneDeep(this.columnsDefinition);
  }

  selectAll(isChecked: boolean): void {
    if (this.hasSelectionFeature) {
      this.dataSource.filteredData.map(row => row.Id).forEach((key: string) => this.selectedRows[key].IsChecked = isChecked);

      if (isChecked === true) {
        this.select();
      }
      else {
        this.onRowSelectionChange.emit([]);
      }

      this.onAllRowSelectionChange.emit(isChecked);
    }
  }

  resetSelection(): void {
    this.initSelectedRows();
    this.onRowSelectionChange.emit([]);
    this.onAllRowSelectionChange.emit(false);
  }

  select(): void {
    if (this.hasSelectionFeature) {
      const selectedIds = Object.entries(this.selectedRows).filter((value) => value[1].IsChecked).map((value) => value[0]);
      this.onRowSelectionChange.emit(selectedIds);
    }
  }

  extractData(data: any, key: string): any {
    const keySegments = key.split('.');

    for (let keySegment of keySegments) {
      if (!_.isNil(data[keySegment])) {
        data = data[keySegment];
      } else {
        return '';
      }
    }
    return data;
  }

  exportAsExcel(): any {
    if (this.serverPagination) {
      this.onExport()
    } else {
      this.excelService.exportAsExcel({
        fileName: `${this.exportFileName}_${moment().format('YYYYMMDo')}`,
        data: this.transformData(this.dataSource.filteredData)
      });
    }
  }

  clearFilters(): void {
    this.applyFilterValue('');
    this.onClearFilters();
  }

  private getColumnsDefinition(): ColumnDefinitionModel[] {

    const configuration = JSON.parse(localStorage.getItem(this.tablePreferenceConfigKey));

    let columnsDefinitionConfig = configuration?.columnsDef ?? configuration;

    if (_.isNil(columnsDefinitionConfig)) {
      return _.orderBy(this.columnsDefinition, a => a.Position);
    }
    columnsDefinitionConfig = columnsDefinitionConfig.filter(c => !_.isNil(c));

    const definedColumnNames = this.columnsDefinition.map(c => c.ColumnName);
    columnsDefinitionConfig = columnsDefinitionConfig.filter(c => definedColumnNames.includes(c.ColumnName));

    columnsDefinitionConfig.forEach((columnDefinition: ColumnDefinitionModel) => {
      const refColumnDefinition = _.find(this.columnsDefinition, c => c.ColumnName == columnDefinition.ColumnName);
      columnDefinition.Pipe = _.cloneDeep(refColumnDefinition.Pipe);
      columnDefinition.ExportPipe = _.cloneDeep(refColumnDefinition.ExportPipe);
      columnDefinition.CanSort = refColumnDefinition.CanSort;
      columnDefinition.DisplayName = refColumnDefinition.DisplayName;
      columnDefinition.CanHide = refColumnDefinition.CanHide;
      columnDefinition.CssClass = refColumnDefinition.CssClass;
      columnDefinition.ToolTip = refColumnDefinition.ToolTip;
    });
    return _.orderBy(columnsDefinitionConfig, a => a.Position);
  }

  private filterOnValue(keyword: string): void {
    if (_.isNil(keyword)) {
      return;
    }

    keyword = keyword.toLowerCase() == 'oui' ? 'true' : keyword.toLowerCase() == 'non' ? 'false' : keyword;
    const trimedKeywordValue = keyword.trim().toLowerCase();
    this.dataSource.filter = trimedKeywordValue;
    if (_.isNil(this.highlights) || !this.highlights.length)
      this.resetSelection();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  private filterByVisibleColumns = (data: any, filter: string): boolean => {
    const visibleColumns = this.columnsDefinition.filter((column: ColumnDefinitionModel) => column.IsVisible && column.Position > 0);
    let entity = Object.getOwnPropertyDescriptors(data);
    for (const visibleColumn of visibleColumns) {
      if (visibleColumn.ColumnName.includes(".")) {
        const path = visibleColumn.ColumnName.split(".");
        if (this.filterByPath(data, filter, path)) {
          return true;
        }
        continue;
      }

      let column = entity[visibleColumn.ColumnName];
      if (_.isNil(column) || _.isNil(column.value)) {
        continue;
      }

      let value: string = `${column.value}`.trim().toLowerCase();
      if (value.includes(filter))
        return true;
    }

    return false;
  }

  private filterByPath(data: any, filter: string, path: string[]): boolean {
    let entity = Object.getOwnPropertyDescriptors(data);
    for (let index = 0; index < path.length - 1; index++) {
      const pathPart = path[index];
      entity = Object.getOwnPropertyDescriptors(entity[pathPart].value);
    }
    let lastColumnName = _.last(path);
    const subEntity = entity[lastColumnName];
    if (_.isNil(subEntity) || _.isNil(subEntity.value)) {
      return false;
    }

    let value: string = `${subEntity.value}`.trim().toLowerCase();
    return value.includes(filter);
  }

  private applyNavigationFilter(): void {

    if (this.persistFilter !== true) {
      this.tableNavigation.Keyword = null;
      return;
    }

    this.filterOnValue(this.tableNavigation.Keyword);

    if (!_.isNil(this.tableNavigation.Page)) {
      this.paginator.pageIndex = this.tableNavigation.Page.pageIndex;
      this.paginator.pageSize = this.tableNavigation.Page.pageSize;
      this.pageSize = this.tableNavigation.Page.pageSize;
    }

    if (!_.isNil(this.tableNavigation.Sort)) {
      this.sort.active = this.tableNavigation.Sort.active;
      this.sort.direction = this.tableNavigation.Sort.direction;
    }
  }

  private transformData(data: any): any {
    const headerMapper = [];
    this.columnsDefinition.filter(x => x.Position > 0).forEach(function (a) {
      headerMapper[a.ColumnName] = _.cloneDeep({ DisplayName: a.DisplayName, Pipe: a.ExportPipe ?? a.Pipe });
    });
    var numericColumns = Object.values(headerMapper).filter(h => h?.Pipe?.PipeName == "moneyPipe").map(c => c.DisplayName);

    const transformedData = data.map(d => {
      let rowTransformedData = [];
      for (const dataPropertyKey of this.extractKeysPath(d)) {
        const columnDefinition = headerMapper[dataPropertyKey];
        if (!_.isNil(columnDefinition) && !_.isNil(columnDefinition.DisplayName) && !this.exportExcludedColumns.includes(dataPropertyKey)) {
          rowTransformedData[columnDefinition.DisplayName] = this.dynamicPipe.transform(this.extractData(d, dataPropertyKey), columnDefinition?.Pipe?.ParamExtractor(d), columnDefinition?.Pipe);
        }
      }
      return rowTransformedData;
    });

    return { rows: transformedData, numericColumns: numericColumns };
  }

  extractKeysPath(data: any) {

    const concatFullPath = (a, b) => a ? `${a}.${b}` : b;

    const paths = (obj = {}, head = '') => {
      return Object.entries(obj)
        .reduce((entry, [key, value]) => {
          let fullPath = concatFullPath(head, key)
          return _.isObject(value) && !_.isArray(value) ?
            entry.concat(paths(value, fullPath))
            : entry.concat(fullPath)
        }, []);
    }

    return paths(data);
  }

  private saveGridFilter(keyword: string): void {
    if (this.persistFilter !== true) {
      return;
    }

    this.sessionStorageService.store(this.pageNavigationStorageKey, { keyword: keyword });
  }

  private getGridNavigationFilters(): TableNavigationModel {
    let tableNavigation = Object.assign({}, JSON.parse(localStorage.getItem(this.tablePreferenceConfigKey))?.pageDef) as TableNavigationModel;
    tableNavigation.Keyword = this.sessionStorageService.get<any>(this.pageNavigationStorageKey)?.keyword;
    return tableNavigation;
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }
}
